import {
  Component,
  Inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
  selectCurrentUser,
  User,
  PL_API_CONFIG,
  PlAPIConfig,
} from 'pl-core-services';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';
import { InternalAdminService } from '@app/common/services';
import { Account } from '@app/common/models/account';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DateTime } from 'luxon';
import { PLExtendAccountDeactivation } from '@app/common/store';
import { FeatureFlagService } from '@app/common/services/feature-flag.service';
import { FeatureFlags } from '@app/common/constant/feature-flags';
import { BillingService } from '@app/common/services/billing.service';
import { concatLatestFrom } from '@ngrx/operators';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AuthSyncService } from '@app/auth/auth-sync.service';
import { environment } from '@environments/environment';
import { take } from 'rxjs/operators';
import { fromSubscription } from '@app/subscription/store';
import { getIsSiriusEnabled } from '@app/common/sirius-util';

@Component({
  selector: 'pl-account-dropdown',
  templateUrl: './pl-account-dropdown.component.html',
  styleUrls: ['./pl-account-dropdown.component.scss'],
})
export class PlAccountDropdownComponent implements OnInit {
  currentUser$: Observable<User> = this.store.select(selectCurrentUser);

  readonly logoutHref;

  readonly changePasswordHref;

  readonly billingAdminURL$: Observable<string>;

  readonly canPurchase$: Observable<boolean>;

  readonly defaultDeactivationDate = DateTime.now()
    .startOf('day')
    .plus({ days: 7 });

  readonly deactivationDate = new UntypedFormControl(
    this.defaultDeactivationDate
  );

  isSiriusEnabled: boolean;

  minDeactivationDate: DateTime = this.defaultDeactivationDate;

  @ViewChild('deactivationModal')
  deactivationModalTemplate: TemplateRef<void>;

  isInternalAdmin: boolean;

  account: Account;

  flag$ = this.flagService.isFeatureEnabled(FeatureFlags.EXTEND_DEACTIVATION);

  constructor(
    @Inject(PL_API_CONFIG) config: PlAPIConfig,
    private dialog: MatDialog,
    private store: Store,
    private flagService: FeatureFlagService,
    public internalAdminService: InternalAdminService,
    private billingService: BillingService,
    private oidcSecurityService: OidcSecurityService,
    private authSyncService: AuthSyncService
  ) {
    this.isSiriusEnabled = getIsSiriusEnabled();
    this.logoutHref = !this.isSiriusEnabled
      ? `${config.login_url}/logout/`
      : null;
    this.changePasswordHref = `${config.login_url}/password/change/`;

    const account$ = this.internalAdminService
      .getLoadedAccount()
      .pipe(map((account) => account));

    this.billingAdminURL$ = combineLatest([
      this.flagService.isFeatureEnabled(FeatureFlags.SUBSCRIPTION_MANAGEMENT),
      this.store.select(fromSubscription.selectIsFreemium),
    ]).pipe(
      concatLatestFrom(() => account$),
      switchMap(([[isFeatureEnabled, isFreemium], account]) => {
        if (isFeatureEnabled && account.type === 'individual' && !isFreemium) {
          return this.billingService.createExternalAdminURL();
        }
        return of('');
      })
    );

    this.canPurchase$ = combineLatest([
      this.flagService.isFeatureEnabled(FeatureFlags.SUBSCRIPTION_MANAGEMENT),
      this.store.select(fromSubscription.selectCanUpgrade),
    ]).pipe(
      concatLatestFrom(() => account$),
      map(
        ([[isFeatureEnabled, isFreemium], account]) =>
          isFeatureEnabled && account.type === 'individual' && isFreemium
      )
    );
  }

  async ngOnInit() {
    this.internalAdminService.getLoadedAccount().subscribe((account) => {
      this.account = account;
      this.minDeactivationDate =
        account?.expiration_date || this.defaultDeactivationDate;
    });
  }

  openModal() {
    this.dialog.open(this.deactivationModalTemplate);
  }

  updateDeactivationDate() {
    this.store.dispatch(
      PLExtendAccountDeactivation({
        date: this.deactivationDate.value,
        accountUuid: this.account.uuid,
      })
    );
  }

  async logout() {
    if (this.isSiriusEnabled) {
      this.oidcSecurityService
        .getIdToken()
        .pipe(take(1))
        .subscribe(async (idToken) => {
          await this.authSyncService.broadcastLogout();

          if (idToken) {
            const encodedIDToken = encodeURIComponent(idToken);
            const postLogoutURI = encodeURIComponent(
              `${window.location.origin}/auth/logged-out`
            );
            const endSessionUrl = `${environment.sirius_url}/oidc/end_session?id_token_hint=${encodedIDToken}&post_logout_redirect_uri=${postLogoutURI}`;

            // NOTE: We clear our the OIDC client's storage before redirecting to the end session URL,
            // `angular-auth-oidc-client` will remove the ID, Access + Refresh token data from its storage
            // leaving information about the well known endpoint and the configuration data in place (which is both
            // reasonable and safe, obviously).
            this.oidcSecurityService.logoffLocal();

            // NOTE: After clearing the OIDC client's storage, we redirect to the end session URL providing the ID token
            // which is - of course - the important bit to actually log the user out of the OIDC provider,
            // in this case our Sirius auth server from whom the ID token (and Access + Refresh) was originally issued.
            // In this step, the auth server will revoke the tokens and clear any session data on its end, redirecting
            // to the requested post logout URI the client provides.
            window.location.href = endSessionUrl;
          } else {
            // NOTE: This is a fallback for the case where the ID token is not available, at least have
            // `angular-auth-oidc-client` clean up its storage...
            this.oidcSecurityService.logoffLocal();
            window.location.href = '/auth/logged-out';
          }
        });
    } else {
      // Handle legacy logout...
      window.location.href = this.logoutHref;
    }
  }
}
